import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Clock, Circle, MapPin, Loader2 } from "lucide-react";
import { api, fileUrl } from "@/api/client";
import { useI18n } from "@/i18n";

const STEPS = ["received", "in_review", "resolved"];

function AuthImage({ path }) {
  const [url, setUrl] = useState(null);
  useEffect(() => { fileUrl(path).then(setUrl); }, [path]);
  if (!url) return <div className="h-24 w-24 animate-pulse rounded-lg bg-brand-tint" />;
  return <img src={url} alt="" className="h-24 w-24 rounded-lg object-cover" />;
}

export default function ComplaintDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api(`/complaints/${ticketId}`).then(setC).catch(() => {}).finally(() => setLoading(false));
  }, [ticketId]);

  if (loading) return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;
  if (!c) return <div className="py-24 text-center text-ink-muted">{ticketId} — not found</div>;

  const stepLabel = (s) => (s === "resolved" ? t("stResolved") : s === "in_review" ? t("stInReview") : t("stReceived"));
  const currentIdx = STEPS.indexOf(c.status);

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1 text-sm font-semibold text-ink-muted hover:text-brand">
        <ArrowLeft size={16} /> {t("back")}
      </button>

      <div className="rounded-3xl border border-line bg-white p-6">
        <div className="text-xs font-bold uppercase tracking-widest text-brand">{c.category}</div>
        <h1 className="mt-1 text-xl font-black text-ink">{c.subject}</h1>
        <div className="mt-1 text-sm text-ink-muted">{c.ticket_id}</div>

        {/* Timeline */}
        <div className="mt-6">
          <h2 className="mb-3 text-xs font-bold uppercase tracking-widest text-ink-muted">{t("trackTitle")}</h2>
          <div className="space-y-3">
            {STEPS.map((s, i) => {
              const reached = i <= currentIdx;
              const isCurrent = i === currentIdx;
              return (
                <div key={s} className="flex items-center gap-3">
                  {reached ? (isCurrent ? <Clock className="text-brand" size={20} /> : <CheckCircle2 className="text-[#2E7D32]" size={20} />) : <Circle className="text-line" size={20} />}
                  <span className={`text-sm font-semibold ${reached ? "text-ink" : "text-ink-muted"}`}>{stepLabel(s)}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-6">
          <h2 className="mb-1.5 text-xs font-bold uppercase tracking-widest text-ink-muted">{t("trackDesc")}</h2>
          <p className="leading-relaxed text-ink-soft">{c.description}</p>
        </div>

        {c.location && (
          <div className="mt-4 flex items-center gap-1.5 text-sm text-ink-muted">
            <MapPin size={14} /> {c.location}
          </div>
        )}

        {c.photo_paths?.length > 0 && (
          <div className="mt-6">
            <h2 className="mb-2 text-xs font-bold uppercase tracking-widest text-ink-muted">{t("trackPhotos")}</h2>
            <div className="flex flex-wrap gap-3">
              {c.photo_paths.map((p) => <AuthImage key={p} path={p} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
